import React, { useRef, useState } from 'react';
import { 
  Drawer,
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space, 
  Avatar, 
  Tag, 
  Timeline, 
  Checkbox,
  Divider,
  message,
  List,
  Mentions,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faPaperclip,
  faCommentDots,
  faSquareCheck,
  faClock,
  faTrash,
  faDownload,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTask, updateTask, deleteTask, PlannerPage } from '../../../services/tasks';
import { getUsers } from '../../../services/users';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { colors } from '../../../theme/colors';
import { priorityChipBaseStyle, priorityChipStyles, tagChipStyle } from '../plannerVisuals';
import { selectActiveMentionOnTab } from '../../../utils/mentions';

const { TextArea } = Input;
const { Option } = Select;

interface TaskDetailModalProps {
  taskId: string;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
  plannerPage?: PlannerPage;
  onResolvedReference?: (reference: string) => void;
}

const getAttachmentId = (attachment: any) => attachment?.id || attachment?._id || attachment?.attachmentId;

interface SelectedMention {
  id: string;
  name: string;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderCommentContent = (content: string, mentionIds: string[] | undefined, users: any[]) => {
  const explicitlyMentionedNames = users
    .filter((user) => mentionIds?.includes(user.id))
    .map((user) => user.name)
    .filter(Boolean);
  const namesPresentInText = users
    .map((user) => user.name)
    .filter((name) => name && content.toLocaleLowerCase().includes(`@${name}`.toLocaleLowerCase()));
  const mentionNames = Array.from(new Set([...explicitlyMentionedNames, ...namesPresentInText]))
    .sort((a, b) => b.length - a.length);

  const mentionSource = mentionNames.length > 0
    ? `@(?:${mentionNames.map(escapeRegExp).join('|')})`
    : '(?!)';
  const tokenPattern = new RegExp(`(https?:\\/\\/[^\\s]+|${mentionSource})`, 'gi');
  const canonicalNames = new Set(mentionNames.map((name) => `@${name}`.toLowerCase()));

  return content.split(tokenPattern).map((part, index) => {
    if (/^https?:\/\//i.test(part)) {
      const url = part.replace(/[),.!?;:]+$/, '');
      const trailingPunctuation = part.slice(url.length);
      return (
        <React.Fragment key={`${part}-${index}`}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#8EC5FF', textDecoration: 'underline', textUnderlineOffset: 2 }}
          >
            {url}
          </a>
          {trailingPunctuation}
        </React.Fragment>
      );
    }

    if (canonicalNames.has(part.toLowerCase())) {
      return (
        <span key={`${part}-${index}`} style={{ color: '#F8B9E3', fontWeight: 600 }}>
          {part}
        </span>
      );
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ taskId, open, onClose, onUpdate, plannerPage, onResolvedReference }) => {
  const [form] = Form.useForm();
  const [commentText, setCommentText] = useState('');
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  const { data: task, isLoading } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => getTask(taskId),
    enabled: open && !!taskId,
  });
  const resolvedTaskId = task?.id || taskId;

  React.useEffect(() => {
    if (!task?.number || !onResolvedReference) return;
    const canonicalReference = `TASK-${task.number}`;
    if (taskId !== canonicalReference) onResolvedReference(canonicalReference);
  }, [onResolvedReference, task?.number, taskId]);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: open,
  });

  const refreshTaskCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    onUpdate();
  };

  const updateTaskMutation = useMutation({
    mutationFn: updateTask,
    onSuccess: () => {
      refreshTaskCaches();
      setIsEditing(false);
      message.success('Task updated successfully');
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: ({ taskId, content, mentionedUserIds }: { taskId: string; content: string; mentionedUserIds: string[] }) =>
      api.post(`/api/tasks/${taskId}/comments`, { content, mentionedUserIds }),
    onSuccess: () => {
      refreshTaskCaches();
      setCommentText('');
      setSelectedMentions([]);
      message.success('Comment added');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to add comment');
    },
  });

  const uploadFileMutation = useMutation({
    mutationFn: async ({ taskId, files }: { taskId: string; files: File[] }) => {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      return api.post(`/api/tasks/${taskId}/attachments`, formData);
    },
    onSuccess: (response) => {
      refreshTaskCaches();
      const failed = response.data?.failed || [];
      const duplicates = response.data?.duplicates || [];
      if (failed.length > 0) {
        message.warning(`${failed.length} file(s) failed to upload`);
      } else if (duplicates.length > 0 && response.data?.data?.length === 0) {
        message.info('File already attached');
      } else if (duplicates.length > 0) {
        message.info(`${duplicates.length} duplicate file(s) skipped`);
      } else {
        message.success('Files uploaded successfully');
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || error.message || 'Failed to upload files');
    },
  });

  const handleDeleteAttachment = async (attachmentId: string, loadingKey = attachmentId) => {
    try {
      setDeletingAttachmentId(loadingKey);
      await api.delete(`/api/tasks/${resolvedTaskId}/attachments/${attachmentId}`);
      refreshTaskCaches();
      message.success('Attachment deleted');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to delete attachment');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      message.success('Task deleted successfully');
      // Invalidate task and tasks lists so board/list views refresh
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onUpdate();
      onClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error || 'Failed to delete task');
    },
  });

  const handleDeleteTask = () => {
    Modal.confirm({
      title: 'Delete this task?',
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => deleteTaskMutation.mutate(resolvedTaskId),
    });
  };

  const handleEditTask = () => {
    form.setFieldsValue({
      title: task?.title,
      description: task?.description,
      status: task?.status,
      priority: task?.priority,
      assigneeId: task?.assigneeId,
      dueDate: task?.dueDate ? dayjs(task.dueDate) : undefined,
      tags: task?.tags?.join(', '),
      swimlane: task?.swimlane || undefined,
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      updateTaskMutation.mutate({
        id: resolvedTaskId,
        ...values,
        dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()) : undefined,
        swimlane: values.swimlane || undefined,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    const mentionedUserIds = selectedMentions
      .filter((mention) => commentText.toLocaleLowerCase().includes(`@${mention.name}`.toLocaleLowerCase()))
      .map((mention) => mention.id);

    addCommentMutation.mutate({ taskId: resolvedTaskId, content: commentText.trim(), mentionedUserIds });
  };

  const handleChecklistToggle = (index: number) => {
    if (!task) return;
    const checklist = [...(task.checklist || [])];
    checklist[index] = { ...checklist[index], completed: !checklist[index].completed };
    updateTaskMutation.mutate({
      id: resolvedTaskId,
      checklist,
    });
  };

  const handleOpenAttachment = async (attachmentId: string, loadingKey = attachmentId) => {
    try {
      setDownloadingAttachmentId(loadingKey);
      const response = await api.get(`/api/tasks/${resolvedTaskId}/attachments/${attachmentId}/content`, {
        responseType: 'blob',
      });
      const responseContentType = response.headers['content-type'];
      const contentType = typeof responseContentType === 'string'
        ? responseContentType
        : 'application/octet-stream';
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: contentType }));
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to open attachment');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (files.length > 0) {
      uploadFileMutation.mutate({ taskId: resolvedTaskId, files });
    }
  };

  if (isLoading || !task) {
    return null;
  }

  const checklist = task.checklist || [];
  const completedCount = checklist.filter((item: any) => item.completed).length;
  const comments = (task as any).comments || [];
  const history = (task as any).history || [];
  const attachments = (task as any).attachmentsFiles || [];

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{isEditing ? 'Edit Task' : 'Task Details'}</span>
          <Tag
            bordered={false}
            style={{
              ...priorityChipBaseStyle,
              ...(priorityChipStyles[task.priority] || priorityChipStyles.MEDIUM),
              margin: 0,
            }}
          >
            {task.priority}
          </Tag>
        </div>
      }
      open={open}
      onClose={onClose}
      closable={false}
      width="min(720px, 100vw)"
      extra={
        <Space>
          <Button
            type="text"
            aria-label="Close task details"
            icon={<FontAwesomeIcon icon={faXmark} />}
            onClick={onClose}
          />
        </Space>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {isEditing ? (
            <>
              <Button onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSave}
                loading={updateTaskMutation.isPending}
                style={{ background: '#DB50AC', borderColor: '#DB50AC' }}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                onClick={handleEditTask}
                style={{ background: '#DB50AC', borderColor: '#DB50AC' }}
              >
                Edit
              </Button>
              <Button
                danger
                aria-label="Delete task"
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={handleDeleteTask}
                loading={deleteTaskMutation.isPending}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      }
    >
      <div>
        {isEditing ? (
          <Form form={form} layout="vertical">
            <Form.Item name="title" label="Title" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <TextArea rows={4} />
            </Form.Item>
            <Form.Item name="status" label="Status">
              <Select>
                {plannerPage?.statuses?.map((status) => (
                  <Option key={status} value={status}>
                    {status}
                  </Option>
                )) || (
                  <Option value={task.status}>{task.status}</Option>
                )}
              </Select>
            </Form.Item>
            {(plannerPage?.swimlanes?.length ?? 0) > 0 && (
              <Form.Item name="swimlane" label="Swimlane">
                <Select placeholder="Select swimlane" allowClear>
                  <Option value="">Unassigned</Option>
                  {plannerPage!.swimlanes!.map((sl) => (
                    <Option key={`${sl.type}:${sl.value}`} value={`${sl.type}:${sl.value}`}>
                      {sl.type}: {sl.value}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            <Form.Item name="assigneeId" label="Assignee">
              <Select
                className="assignee-search-select"
                placeholder="Select assignee"
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = option?.label as string;
                  return label?.toLowerCase().includes(input.toLowerCase()) ?? false;
                }}
                options={users.map((user) => ({
                  value: user.id,
                  label: user.name,
                  user: user,
                }))}
                optionRender={(option) => (
                  <Space>
                    <Avatar size="small" src={option.data.user?.photoUrl} icon={<FontAwesomeIcon icon={faUser} />} />
                    {option.data.label}
                  </Space>
                )}
              />
            </Form.Item>
            <Form.Item name="priority" label="Priority">
              <Select>
                <Option value="LOW">Low</Option>
                <Option value="MEDIUM">Medium</Option>
                <Option value="HIGH">High</Option>
                <Option value="URGENT">Urgent</Option>
              </Select>
            </Form.Item>
            <Form.Item name="dueDate" label="Due Date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="tags" label="Tags">
              <Input placeholder="Comma-separated tags" />
            </Form.Item>
          </Form>
        ) : (
          <>
            {/* Task Info */}
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ color: '#F2F2F2', marginBottom: 8 }}>{task.title}</h2>
              {task.description && (
                <p style={{ color: '#BFBFBF', marginBottom: 16, whiteSpace: 'pre-wrap' }}>
                  {task.description}
                </p>
              )}

              <Space wrap style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faClock} style={{ color: '#BFBFBF' }} />
                  <span style={{ color: '#BFBFBF' }}>
                    {task.dueDate 
                      ? dayjs(task.dueDate).format('MMM D, YYYY')
                      : 'No due date'
                    }
                  </span>
                </div>
                {task.assignee && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FontAwesomeIcon icon={faUser} style={{ color: '#BFBFBF' }} />
                    <Avatar size="small" src={task.assignee.photoUrl}>
                      {task.assignee.name.charAt(0)}
                    </Avatar>
                    <span style={{ color: '#BFBFBF' }}>{task.assignee.name}</span>
                  </div>
                )}
              </Space>

              {task.tags && task.tags.length > 0 && (
                <div
                  aria-label="Task tags"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}
                >
                  {task.tags.map((tag: string, idx: number) => (
                    <Tag key={idx} bordered={false} style={{ ...tagChipStyle, margin: 0 }}>
                      {tag}
                    </Tag>
                  ))}
                </div>
              )}
            </div>

            <Divider />

            {/* Checklist */}
            {checklist.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <FontAwesomeIcon icon={faSquareCheck} style={{ color: '#DB50AC' }} />
                  <h3 style={{ color: '#F2F2F2', margin: 0 }}>
                    Checklist ({completedCount}/{checklist.length})
                  </h3>
                </div>
                <div style={{ 
                  width: '100%', 
                  height: 6, 
                  background: '#2a2a2a', 
                  borderRadius: 3,
                  marginBottom: 12,
                }}>
                  <div style={{
                    width: `${(completedCount / checklist.length) * 100}%`,
                    height: '100%',
                    background: '#DB50AC',
                    borderRadius: 3,
                  }} />
                </div>
                {checklist.map((item: any, index: number) => (
                  <div 
                    key={index}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8,
                      padding: '8px 0',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleChecklistToggle(index)}
                  >
                    <Checkbox checked={item.completed} />
                    <span style={{ 
                      color: item.completed ? '#BFBFBF' : '#F2F2F2',
                      textDecoration: item.completed ? 'line-through' : 'none',
                    }}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <Divider />

            {/* Attachments */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FontAwesomeIcon icon={faPaperclip} style={{ color: '#DB50AC' }} />
                  <h3 style={{ color: '#F2F2F2', margin: 0 }}>
                    Attachments ({attachments.length})
                  </h3>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleUploadInputChange}
                />
                <Button
                  icon={<FontAwesomeIcon icon={faPaperclip} />}
                  loading={uploadFileMutation.isPending}
                  disabled={uploadFileMutation.isPending}
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Files
                </Button>
              </div>
              
              {attachments.length > 0 ? (
                <List
                  dataSource={attachments}
                  renderItem={(attachment: any, index: number) => {
                    const attachmentId = getAttachmentId(attachment);
                    const rowKey = attachmentId || `${attachment.fileName || 'attachment'}-${attachment.createdAt || index}`;

                    return (
                    <List.Item
                      style={{ 
                        background: '#1a1a1a', 
                        borderRadius: 8, 
                        marginBottom: 8,
                        padding: '12px',
                      }}
                      actions={[
                        <Button
                          key="download"
                          type="text"
                          icon={<FontAwesomeIcon icon={faDownload} />}
                          size="small"
                          loading={downloadingAttachmentId === rowKey}
                          disabled={!attachmentId}
                          onClick={() => {
                            if (attachmentId) {
                              handleOpenAttachment(attachmentId, rowKey);
                            }
                          }}
                        />,
                        <Button
                          key="delete"
                          type="text"
                          danger
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          size="small"
                          loading={deletingAttachmentId === rowKey}
                          disabled={!attachmentId}
                          onClick={() => {
                            if (attachmentId) {
                              handleDeleteAttachment(attachmentId, rowKey);
                            }
                          }}
                        />,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<FontAwesomeIcon icon={faPaperclip} style={{ fontSize: 20, color: '#DB50AC' }} />}
                        title={
                          <a
                            href="#attachment"
                            onClick={(event) => {
                              event.preventDefault();
                              if (attachmentId) {
                                handleOpenAttachment(attachmentId, rowKey);
                              }
                            }}
                            style={{ color: '#F2F2F2' }}
                          >
                            {attachment.fileName}
                          </a>
                        }
                        description={
                          <span style={{ color: '#BFBFBF', fontSize: 12 }}>
                            {attachment.fileSize ? `${(attachment.fileSize / 1024).toFixed(2)} KB` : ''}
                            {' • '}
                            {dayjs(attachment.createdAt).format('MMM D, YYYY')}
                            {' • '}
                            {attachment.uploader?.name || 'Unknown'}
                          </span>
                        }
                      />
                    </List.Item>
                    );
                  }}
                />
              ) : (
                <div style={{ color: '#BFBFBF', textAlign: 'center', padding: 20 }}>
                  No attachments yet
                </div>
              )}
            </div>

            <Divider />

            {/* Comments */}
            <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <FontAwesomeIcon icon={faCommentDots} style={{ color: '#DB50AC' }} />
                <h3 style={{ color: '#F2F2F2', margin: 0 }}>
                  Comments ({comments.length})
                </h3>
              </div>
              
              <div style={{ marginBottom: 12 }}>
                <Mentions
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  placeholder="Add a comment — type @ to mention someone"
                  value={commentText}
                  onKeyDown={selectActiveMentionOnTab}
                  onChange={(value) => {
                    setCommentText(value);
                    setSelectedMentions((current) =>
                      current.filter((mention) =>
                        value.toLocaleLowerCase().includes(`@${mention.name}`.toLocaleLowerCase())
                      )
                    );
                  }}
                  onSelect={(option: any) => {
                    const selectedUser = users.find((user) => user.id === option.userId);
                    if (!selectedUser) return;
                    setSelectedMentions((current) =>
                      current.some((mention) => mention.id === selectedUser.id)
                        ? current
                        : [...current, { id: selectedUser.id, name: selectedUser.name }]
                    );
                  }}
                  options={users.map((user) => ({
                    key: user.id,
                    value: user.name,
                    userId: user.id,
                    label: (
                      <Space size={8}>
                        <Avatar size={24} src={user.photoUrl}>{user.name.charAt(0)}</Avatar>
                        <span>{user.name}</span>
                        <span style={{ color: colors.textMuted, fontSize: 12 }}>{user.email}</span>
                      </Space>
                    ),
                  }))}
                />
                <div style={{ color: colors.textMuted, fontSize: 12, marginTop: 6 }}>
                  Mentioned people receive an in-app notification and an email.
                </div>
                <Button
                  type="default"
                  onClick={handleAddComment}
                  loading={addCommentMutation.isPending}
                  disabled={!commentText.trim()}
                  style={{ marginTop: 8 }}
                >
                  Add Comment
                </Button>
              </div>

              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {comments.map((comment: any) => (
                  <div key={comment.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #2a2a2a' }}>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <Avatar src={comment.user?.photoUrl}>
                        {comment.user?.name?.charAt(0)}
                      </Avatar>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ color: '#F2F2F2', fontWeight: 600 }}>
                            {comment.user?.name}
                          </span>
                          <span style={{ color: '#BFBFBF', fontSize: 12 }}>
                            {dayjs(comment.createdAt).format('MMM D, h:mm A')}
                          </span>
                        </div>
                        <p style={{ color: '#BFBFBF', margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                          {renderCommentContent(comment.content, comment.mentions, users)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div style={{ color: '#BFBFBF', textAlign: 'center', padding: 20 }}>
                    No comments yet
                  </div>
                )}
              </div>
            </div>

            <Divider />

            {/* Activity History */}
            {history.length > 0 && (
              <div>
                <h3 style={{ color: '#F2F2F2', marginBottom: 12 }}>Activity</h3>
                <Timeline>
                  {history.map((item: any) => (
                    <Timeline.Item key={item.id} color="#DB50AC">
                      <div style={{ color: '#BFBFBF', fontSize: 12 }}>
                        <strong style={{ color: '#F2F2F2' }}>{item.user?.name}</strong>
                        {' '}
                        {item.action === 'created' && 'created this task'}
                        {item.action === 'moved' && `moved from ${item.oldValue} to ${item.newValue}`}
                        {item.action === 'assigned' && `assigned to ${item.newValue || 'unassigned'}`}
                        {item.action === 'updated' && `updated ${item.field}`}
                        {item.action === 'commented' && 'added a comment'}
                        {' '}
                        <span style={{ color: '#666' }}>
                          {dayjs(item.createdAt).format('MMM D, h:mm A')}
                        </span>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default TaskDetailModal;
